import React, { useRef } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/components/ui/use-toast";
import { useAuth, useUser } from "@clerk/react-router";
import { useMutation } from "@tanstack/react-query";
import {
  CoinsIcon,
  HeartIcon,
  HelpCircleIcon,
  LinkIcon,
  MessageCircleIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  SquareArrowOutUpRight,
  XIcon,
} from "lucide-react";
import { Link } from "react-router";
import { useState } from "react";
import { Spinner } from "../add/utils";
import { clientRPC } from "@/utils/hono-client";
import { usePostHog } from "posthog-js/react";
import { useSidebarCollapse } from "./context";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useLocalStorage } from "../hooks/use-local-storage";
import { Textarea } from "../ui/textarea";

export const LeaveFeedbackDialog = ({
  isCollapsed,
}: {
  isCollapsed?: boolean;
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { userId } = useAuth();
  const { toast } = useToast();
  const [feedback, setFeedback] = useState("");
  const posthog = usePostHog();

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: (newFeedback: string) => {
      return clientRPC.api["feedback"].$post({
        json: { feedback: newFeedback },
      });
    },
    onSuccess: () => {
      toast({
        title: `Thank you for your feedback! Successfully submitted.`,
        variant: "default",
      });
      setDialogOpen(false);
    },
    onError: () => {
      toast({
        title: `Something went wrong. Please report the issue on Discord.`,
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    posthog.capture("Sidebar-Submit-Feedback");
    mutate(`Sidebar\nUser: ${userId}\n${feedback}`);
  };

  return (
    <>
      <Tooltip open={isCollapsed && undefined}>
        <TooltipTrigger asChild>
          <Button
            variant="strokeSecondarySidebar"
            size={isCollapsed ? "iconSm" : "sm"}
            onClick={() => setDialogOpen(true)}
            className="justify-start text-xs"
          >
            <MessageCircleIcon className="w-icon-md h-icon-md" />
            {isCollapsed ? "" : "Feedback"}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">Feedback</TooltipContent>
      </Tooltip>
      <Dialog open={dialogOpen} onOpenChange={(open) => setDialogOpen(open)}>
        <DialogContent className="sm:max-w-[900px]">
          <p className="font-semibold">
            Thank you for considering leaving us a feedback!
          </p>

          <form onSubmit={onSubmit}>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Your feedback"
              className="h-32"
            />
            <p className="font-semibold">
              Encountering an issue? Email us at stefandjokovic@flashka.ai !
            </p>

            <div className="flex justify-end gap-2">
              <Button type="submit" disabled={isLoading} variant="primary">
                {isLoading ? <Spinner /> : "Submit"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const HelpDialog = ({ isCollapsed }: { isCollapsed?: boolean }) => {
  return (
    <Tooltip open={isCollapsed && undefined}>
      <TooltipTrigger asChild>
        <Link to="/help" className="w-full">
          <Button
            variant="strokeSecondarySidebar"
            className="text-xs w-full justify-start"
            size={isCollapsed ? "iconSm" : "sm"}
          >
            <HelpCircleIcon className="h-icon-md w-icon-md" />
            {isCollapsed ? "" : "Help"}
          </Button>
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right">Help</TooltipContent>
    </Tooltip>
  );
};

const useIsUserPaying = () => {
  const { user } = useUser();

  return (
    user?.publicMetadata["status"] === "veteran" ||
    user?.publicMetadata["status"] === "master" ||
    user?.publicMetadata["status"] === "wizard" ||
    user?.publicMetadata["status"] === "beast"
  );
};

export const ShareWithFriendsDialog = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const isUserPaying = useIsUserPaying();
  const { toast } = useToast();
  const posthog = usePostHog();
  const [hasDismissed, setHasDismissed] = useLocalStorage(
    "sidebar-refer-a-friend-dismissed",
    false
  );

  const openDialog = () => {
    setDialogOpen(true);
    posthog.capture("Sidebar-Share-With-Friend-Open-Dialog");
  };

  const handleCopyLink = () => {
    posthog.capture("Sidebar-Share-With-Friend-Link-Copied");
    navigator.clipboard
      .writeText("https://www.flashka.ai")
      .then(() => {
        toast({
          title: "Link copied to clipboard!",
          variant: "default",
        });
        setDialogOpen(false);
      })
      .catch(() => {
        toast({
          title: "Failed to copy link. Please try again.",
          variant: "destructive",
        });
      });
  };

  return (
    <>
      {isUserPaying && !hasDismissed ? (
        <div
          className="w-full h-full border rounded-2xl border-secondary-200 relative p-2 flex flex-col gap-2"
          onClick={() => {
            setDialogOpen(true);
            posthog.capture("Sidebar-Refer-A-Friend-Open-Dialog");
          }}
        >
          <Button
            className="absolute top-2 right-2"
            size="iconSm"
            onClick={(e) => {
              e.stopPropagation();
              setHasDismissed(true);
            }}
          >
            <XIcon />
          </Button>
          <div className="flex items-center justify-center">
            <div className="w-[160px] h-[160px] overflow-hidden">
              <DotLottieReact
                src="https://lottie.host/080e1933-f534-4e3e-b468-3368d4d5f028/2S3O0pmdUP.lottie"
                autoplay
                loop
                className="h-[360px] w-[360px] -mt-24 -ml-24"
              />
            </div>
          </div>
          <div className="font-semibold text-lg text-center text-title-950">
            Earn up to <span className="text-primary-500">$12</span> for every
            friend you invite
          </div>
          <Button className="w-full" variant="primary">
            <HeartIcon />
            Refer a friend
          </Button>
        </div>
      ) : (
        <Button
          variant="strokeSecondarySidebar"
          size="sm"
          onClick={openDialog}
          className="justify-start text-xs"
        >
          <SquareArrowOutUpRight className="h-icon-md w-icon-md" />
          Share with a friend
        </Button>
      )}
      <Dialog open={dialogOpen} onOpenChange={(open) => setDialogOpen(open)}>
        <DialogContent className="sm:max-w-[700px] z-[200]">
          {isUserPaying ? (
            <DialogPayingUser
              closeDialog={() => {
                posthog.capture("Sidebar-Referral-Program-Dialog-Close");
                setDialogOpen(false);
              }}
            />
          ) : (
            <>
              <p className="font-semibold text-2xl text-center">
                Share Flashka with your friends 📚
              </p>
              <p className="text-center">
                Share Flashka with your study buddy or group and study together!
              </p>
              <div className="w-full flex items-center justify-center gap-2">
                <Button
                  className="w-full"
                  variant="secondary"
                  onClick={() => setDialogOpen(false)}
                >
                  <XIcon className="mr-1" /> Cancel
                </Button>
                <Button
                  className="w-full"
                  variant="primary"
                  onClick={handleCopyLink}
                >
                  <LinkIcon className="mr-1" /> Copy Link
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const DialogPayingUser = ({ closeDialog }: { closeDialog: () => void }) => {
  const posthog = usePostHog();

  return (
    <div className="flex flex-col items-center justify-center gap-2 pt-2 md:pt-8">
      <p className="font-bold text-4xl text-center text-title-950">
        Give <span className="text-primary-500">20%</span> off.
      </p>
      <p className="text-4xl text-center text-title-950 font-bold mb-2">
        Earn <span className="text-primary-500">20%</span> commission.
      </p>
      <p className="text-center">
        Let’s do some quick math with an example:
        <br />
        Your friend pays{" "}
        <span className="font-semibold">$57 (instead of $72)</span> for Wizard
        Yearly. You earn <span className="font-semibold">$12</span> for every
        friend.
      </p>
      <div className="bg-secondary-100 p-3 rounded-lg text-body-600 w-full mt-4">
        All you have to do is:
        <br />✅ Click the button below and create an account on{" "}
        <span className="font-semibold">Rewardful</span>
        <br />✅ Link your <span className="font-semibold">PayPal</span> account
        where you’ll receive your commissions
        <br />✅ Share your unique{" "}
        <span className="font-semibold">Coupon Code</span> (eg. MYBUDDY) to your
        friends
        <br />✅ <span className="font-semibold">Earn 20% commission</span> for
        every referral directly on your PayPal
      </div>
      <div className="flex gap-2 w-full items-center justify-center mt-8">
        <Button
          variant="secondary"
          className="w-full"
          onClick={() => {
            closeDialog();
          }}
        >
          Maybe later
        </Button>
        <Link
          to="https://flashka-ou.getrewardful.com/signup?campaign=flashkabuddies"
          className="w-full"
          target="_blank"
        >
          <Button
            variant="primary"
            className="w-full"
            onClick={() => {
              posthog.capture("Sidebar-Referral-Program-Dialog-Join-Earn");
            }}
          >
            <CoinsIcon className="mr-1" /> Join & Start Earning
          </Button>
        </Link>
      </div>
    </div>
  );
};

export function AllDecksIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      className={className}
      role="img"
      aria-label="View all decks"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Frame 562">
        <g id="Group 445">
          <g id="Vector">
            <path
              d="M3 6.62963L3 15.7037C3 16.4196 3.58037 17 4.2963 17L13.3704 17C14.0863 17 14.6667 16.4196 14.6667 15.7037L14.6667 6.62963C14.6667 5.91371 14.0863 5.33333 13.3704 5.33333L4.2963 5.33333C3.58037 5.33333 3 5.91371 3 6.62963Z"
              fill="#F5F5F5"
            />
            <path d="M3 11.1667L14.6667 11.1667L3 11.1667Z" fill="#F5F5F5" />
            <path
              d="M3 11.1667L14.6667 11.1667M3 15.7037L3 6.62963C3 5.91371 3.58037 5.33333 4.2963 5.33333L13.3704 5.33333C14.0863 5.33333 14.6667 5.91371 14.6667 6.62963L14.6667 15.7037C14.6667 16.4196 14.0863 17 13.3704 17L4.2963 17C3.58037 17 3 16.4196 3 15.7037Z"
              stroke="currentColor"
              strokeWidth="1.67"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
          <g id="Vector_2">
            <path
              d="M5.33398 4.29662L5.33398 13.3707C5.33398 14.0866 5.91436 14.667 6.63028 14.667L15.7044 14.667C16.4203 14.667 17.0006 14.0866 17.0006 13.3707L17.0006 4.29662C17.0006 3.5807 16.4203 3.00033 15.7044 3.00033L6.63028 3.00033C5.91436 3.00033 5.33398 3.5807 5.33398 4.29662Z"
              fill="#F5F5F5"
            />
            <path
              d="M5.33398 8.83366L17.0006 8.83366L5.33398 8.83366Z"
              fill="#F5F5F5"
            />
            <path
              d="M5.33398 8.83366L17.0006 8.83366M5.33398 13.3707L5.33398 4.29662C5.33398 3.5807 5.91436 3.00033 6.63028 3.00033L15.7044 3.00033C16.4203 3.00033 17.0006 3.5807 17.0006 4.29662L17.0006 13.3707C17.0006 14.0866 16.4203 14.667 15.7044 14.667L6.63028 14.667C5.91436 14.667 5.33398 14.0866 5.33398 13.3707Z"
              stroke="currentColor"
              strokeWidth="1.67"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </g>
      </g>
    </svg>
  );
}

export const SidebarExpandCollapseButton = () => {
  const { isCollapsed, setIsCollapsed, setOpenStatus } = useSidebarCollapse();
  const posthog = usePostHog();
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Create ref for timeout ID

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    // Set a new timeout
    hoverTimeoutRef.current = setTimeout(() => {
      setOpenStatus("hover");
    }, 200); // 100ms delay
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    hoverTimeoutRef.current = setTimeout(() => {
      setOpenStatus("closed");
    }, 200); // 100ms delay
  };

  return (
    <Button
      variant="strokeSecondary"
      size="iconMd"
      onClick={() => {
        setIsCollapsed(!isCollapsed);
        setOpenStatus("closed");
        if (isCollapsed) {
          posthog.capture("Sidebar-Collapse-Open");
        } else {
          posthog.capture("Sidebar-Collapse-Close");
        }
      }}
      className="flex-shrink-0"
      onMouseEnter={handleMouseEnter} // Use the new handler
      onMouseLeave={handleMouseLeave}
      // HACK: we need to delay the touch end event to avoid it from conflicting with the mouseEnter event on touch screens
      onTouchEnd={() => setTimeout(handleMouseLeave, 100)}
    >
      {isCollapsed ? (
        <PanelLeftOpenIcon className="h-icon-md w-icon-md" />
      ) : (
        <PanelLeftCloseIcon className="h-icon-md w-icon-md" />
      )}
    </Button>
  );
};
